<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import TelegramInternalApi from 'dashboard/api/telegramInternal';
import { parseKeywords, stringifyKeywords } from '../telegramInternal.helpers';

const { t } = useI18n();
const store = useStore();

const agents = ref([]);

const botToken = ref('');
const autoImportEnabled = ref(true);
const allowlistText = ref('');
const categories = ref([]);
const myTelegramHandle = ref('');
const discoveredGroups = ref([]);
const savedAt = ref(null);
const canManageGlobalSettings = ref(false);
const isSaving = ref(false);

const loadSettings = async () => {
  const response = await TelegramInternalApi.getSettings();
  const data = response.data || {};
  botToken.value = data.bot_token || '';
  autoImportEnabled.value =
    data.auto_import_enabled === undefined
      ? true
      : Boolean(data.auto_import_enabled);
  allowlistText.value = Array(data.allowlist || []).join('\n');
  categories.value = Array(data.categories || []).map(category => ({
    id: category.id,
    name: category.name,
    ownerAgentId: category.owner_agent_id
      ? Number(category.owner_agent_id)
      : null,
    keywordText: stringifyKeywords(category.keywords),
  }));
  myTelegramHandle.value = data.my_telegram_handle || '';
  discoveredGroups.value = Array(data.discovered_groups || []);
  canManageGlobalSettings.value = Boolean(data.can_manage_global_settings);
};

const saveSettings = async () => {
  isSaving.value = true;
  const allowlist = allowlistText.value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

  const normalizedCategories = categories.value.map(category => ({
    id: category.id,
    name: category.name,
    owner_agent_id: category.ownerAgentId
      ? Number(category.ownerAgentId)
      : null,
    keywords: parseKeywords(category.keywordText),
  }));

  try {
    const payload = {
      my_telegram_handle: myTelegramHandle.value.trim(),
    };
    if (canManageGlobalSettings.value) {
      payload.bot_token = botToken.value.trim();
      payload.auto_import_enabled = autoImportEnabled.value;
      payload.allowlist = allowlist;
      payload.categories = normalizedCategories;
    }
    await TelegramInternalApi.updateSettings(payload);
    savedAt.value = Date.now();
    await loadSettings();
  } finally {
    isSaving.value = false;
  }
};

const addCategory = () => {
  const id = `category-${Date.now()}`;
  categories.value = [
    ...categories.value,
    {
      id,
      name: '',
      ownerAgentId: null,
      keywordText: '',
      keywords: [],
    },
  ];
};

const removeCategory = categoryId => {
  categories.value = categories.value.filter(
    category => category.id !== categoryId
  );
};

onMounted(async () => {
  await store.dispatch('agents/get');
  agents.value = store.getters['agents/getAgents'] || [];
  await loadSettings();
});
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.BOT.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.BOT.HINT') }}
      </p>
      <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label
          class="flex flex-col gap-1 text-sm text-n-slate-12 md:col-span-2"
        >
          {{ t('TELEGRAM_INTERNAL.SETTINGS.BOT.TOKEN') }}
          <input
            v-model="botToken"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
            :placeholder="t('TELEGRAM_INTERNAL.SETTINGS.BOT.TOKEN_PLACEHOLDER')"
          />
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="autoImportEnabled" type="checkbox" />
          {{ t('TELEGRAM_INTERNAL.SETTINGS.BOT.AUTO_IMPORT') }}
        </label>
      </div>
    </div>

    <div
      v-if="canManageGlobalSettings"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
    >
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.ALLOWLIST.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.ALLOWLIST.HINT') }}
      </p>
      <label class="mt-3 flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.ALLOWLIST.INPUT') }}
        <textarea
          v-model="allowlistText"
          rows="4"
          class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          :placeholder="t('TELEGRAM_INTERNAL.SETTINGS.ALLOWLIST.PLACEHOLDER')"
        />
      </label>
    </div>

    <div
      v-if="canManageGlobalSettings"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
    >
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.DISCOVERED_GROUPS.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.DISCOVERED_GROUPS.HINT') }}
      </p>
      <div class="mt-3 overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-n-alpha-2 text-left text-n-slate-11">
            <tr>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.DISCOVERED_GROUPS.GROUP') }}
              </th>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.DISCOVERED_GROUPS.CHAT_ID') }}
              </th>
              <th class="px-3 py-2">
                {{
                  t('TELEGRAM_INTERNAL.SETTINGS.DISCOVERED_GROUPS.LAST_SEEN')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="group in discoveredGroups"
              :key="group.id"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 text-n-slate-12">
                {{ group.title || group.username || `#${group.id}` }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">{{ group.id }}</td>
              <td class="px-3 py-2 text-n-slate-12">
                {{
                  group.last_seen_at
                    ? new Date(group.last_seen_at).toLocaleString()
                    : '-'
                }}
              </td>
            </tr>
            <tr v-if="!discoveredGroups.length">
              <td class="px-3 py-6 text-n-slate-11" colspan="3">
                {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="canManageGlobalSettings"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
    >
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.AGENT_LINK.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.AGENT_LINK.HINT') }}
      </p>
      <label
        class="mt-3 flex flex-col gap-1 text-sm text-n-slate-12 md:max-w-md"
      >
        {{ t('TELEGRAM_INTERNAL.SETTINGS.AGENT_LINK.MY_HANDLE') }}
        <input
          v-model="myTelegramHandle"
          class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          :placeholder="
            t('TELEGRAM_INTERNAL.SETTINGS.AGENT_LINK.MY_HANDLE_PLACEHOLDER')
          "
        />
      </label>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <div class="flex items-center justify-between">
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.TITLE') }}
        </h2>
        <button
          type="button"
          class="rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12"
          @click="addCategory"
        >
          {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.ADD') }}
        </button>
      </div>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.HINT') }}
      </p>

      <div class="mt-3 overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-n-alpha-2 text-left text-n-slate-11">
            <tr>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.NAME') }}
              </th>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.KEYWORDS') }}
              </th>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.OWNER') }}
              </th>
              <th class="px-3 py-2">
                {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="category in categories"
              :key="category.id"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2">
                <input
                  v-model="category.name"
                  class="w-full rounded-md border border-n-weak bg-n-background px-2 py-1"
                />
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="category.keywordText"
                  class="w-full rounded-md border border-n-weak bg-n-background px-2 py-1"
                  :placeholder="
                    t(
                      'TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.KEYWORD_PLACEHOLDER'
                    )
                  "
                />
              </td>
              <td class="px-3 py-2">
                <select
                  v-model="category.ownerAgentId"
                  class="w-full rounded-md border border-n-weak bg-n-background px-2 py-1"
                >
                  <option :value="null">
                    {{ t('TELEGRAM_INTERNAL.QUEUE.UNASSIGNED') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="Number(agent.id)"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </td>
              <td class="px-3 py-2">
                <button
                  type="button"
                  class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12"
                  @click="removeCategory(category.id)"
                >
                  {{ t('TELEGRAM_INTERNAL.SETTINGS.CATEGORIES.REMOVE') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex items-center justify-between pb-6">
      <p class="text-xs text-n-slate-11">
        <span v-if="savedAt">
          {{ t('TELEGRAM_INTERNAL.SETTINGS.SAVED_AT') }}:
          {{ new Date(savedAt).toLocaleString() }}
        </span>
      </p>
      <button
        type="button"
        class="rounded-md bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        :disabled="isSaving"
        @click="saveSettings"
      >
        {{
          isSaving
            ? t('TELEGRAM_INTERNAL.SETTINGS.SAVING')
            : t('TELEGRAM_INTERNAL.SETTINGS.SAVE')
        }}
      </button>
    </div>
  </div>
</template>
